import type { InvoiceClassificationResult } from './types'

export const DEFAULT_INVOICE_KEYWORDS = {
    en: [
        'invoice',
        'receipt',
        'bill',
        'statement',
        'payment',
        'purchase',
        'amount',
        'total',
        'subtotal',
        'tax',
        'vat',
        'gst',
        'booking',
        'ticket',
        'subscription',
    ],
    de: [
        'rechnung',
        'quittung',
        'rechnung',
        'abrechnung',
        'kauf',
        'zahlung',
        'betrag',
        'gesamt',
        'zwischensumme',
        'steuer',
        'mwst',
        'ust',
        'buchung',
        'ticket',
        'bestellbestätigung',
    ],
    es: [
        'factura',
        'recibo',
        'cuenta',
        'estado',
        'compra',
        'pago',
        'importe',
        'total',
        'subtotal',
        'impuesto',
        'iva',
        'igic',
        'booking',
        'ticket',
    ],
    fr: [
        'facture',
        'reçu',
        'facture',
        'relevé',
        'achat',
        'paiement',
        'montant',
        'total',
        'sous-total',
        'taxe',
        'tva',
        'tps',
    ],
    it: [
        'fattura',
        'ricevuta',
        'conto',
        'acquisto',
        'estratto',
        'pagamento',
        'importo',
        'totale',
        'subtotale',
        'tassa',
        'iva',
        'igv',
        'booking',
        'ticket',
    ],
    pt: [
        'fatura',
        'recibo',
        'conta',
        'extrato',
        'compra',
        'pagamento',
        'valor',
        'total',
        'subtotal',
        'imposto',
        'iva',
        'icms',
        'booking',
        'ticket',
    ],
    nl: [
        'factuur',
        'bon',
        'aankoop',
        'rekening',
        'afschrift',
        'betaling',
        'bedrag',
        'totaal',
        'subtotaal',
        'belasting',
        'btw',
        'omzetbelasting',
        'booking',
        'ticket',
    ],
} as const

/**
 * Given the text of an email this attempts to classify it as an invoice or not by using some crude heuristics.
 */
export function classifyEmailAsInvoice(
    emailText: string,
): InvoiceClassificationResult {
    const processedText = emailText.toLowerCase()

    // Count keyword occurrences across all languages
    let keywordCount = 0
    for (const languageKeywords of Object.values(DEFAULT_INVOICE_KEYWORDS)) {
        for (const keyword of languageKeywords) {
            const matches = processedText.match(new RegExp(`${keyword}`, 'gi'))
            if (matches) {
                keywordCount += 1
            }
        }
    }

    // Check for some simple heuristics for additional confidence measures:
    let heuristicBonus = 0
    // Currency symbols
    if (/\$|€|£|¥|₹|₽|₩|₪|₦|₨|₱|₲|₴|₵|₸|₺|₼|₾|₿/.test(processedText)) {
        heuristicBonus += 0.2
    }
    // Amount with decimal
    if (/\d+\.\d{2}/.test(processedText)) {
        heuristicBonus += 0.1
    }
    // Date patterns
    if (/\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}/.test(processedText)) {
        heuristicBonus += 0.1
    }

    // Base confidence from keywords (0.1 per occurrence, capped at 0.6)
    const keywordConfidence = Math.min(keywordCount * 0.5, 0.6)
    // Add heuristic bonus (capped at 0.4)
    const totalConfidence = Math.min(keywordConfidence + heuristicBonus, 1.0)

    return {
        classification: totalConfidence >= 0.5 ? 'invoice' : 'other',
        confidence: totalConfidence,
    }
}
